import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import EmployeePunchIn from './EmployeeClockIn'
import ActionButtonRow from './ui/ActionButtonRow'
import EmptyState from './ui/EmptyState'
import ErrorState from './ui/ErrorState'
import FilterBar from './ui/FilterBar'
import LoadingState from './ui/LoadingState'
import {
  TemplateFormShell,
  TemplateHeaderBlock,
  TemplatePage,
  TemplateSectionCard,
  TemplateSummaryRow,
  TemplateTableShell
} from './ui/PageTemplate'
import StatPill from './ui/StatPill'
import StatusBadge from './ui/StatusBadge'
import { buildSchemaUnavailableMessage, isSchemaMismatchError } from './admin/schemaGuard'
import { getDateKeyInTimeZone } from './time/punchDomain'
import { formatTitleText } from './utils/displayText'

const ROLE_MANAGER = 'manager'
const ROLE_SHIFT_LEAD = 'shift_lead'
const ROLE_ADMIN = 'admin'
const ROLE_OPERATIONS_SUPPORT = 'operations_support'

const formatDateTime = (value) => {
  if (!value) return 'Unknown time'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown time'
  return date.toLocaleString()
}

const toDateTimeLocalValue = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

const toIsoOrNull = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

export default function ManagerTimeAdmin({ userRole = '', userWorkerId = '', isAdminPreview = false }) {
  const normalizedRole = String(userRole || '').toLowerCase()
  const isReviewerRole = normalizedRole === ROLE_MANAGER || normalizedRole === ROLE_SHIFT_LEAD || normalizedRole === ROLE_OPERATIONS_SUPPORT
  const hasWorkspaceAccess = normalizedRole === ROLE_ADMIN || isReviewerRole
  const missingBinding = isReviewerRole && !isAdminPreview && !userWorkerId

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [unavailableMessage, setUnavailableMessage] = useState('')
  const [workersById, setWorkersById] = useState(new Map())
  const [jobsById, setJobsById] = useState(new Map())
  const [entries, setEntries] = useState([])
  const [filterDate, setFilterDate] = useState(getDateKeyInTimeZone(new Date(), 'America/Detroit'))
  const [editingEntryId, setEditingEntryId] = useState('')
  const [editClockedInAt, setEditClockedInAt] = useState('')
  const [editClockedOutAt, setEditClockedOutAt] = useState('')
  const [editReason, setEditReason] = useState('')
  const [editNote, setEditNote] = useState('')
  const [editError, setEditError] = useState('')
  const [editSaving, setEditSaving] = useState(false)

  const fetchData = useCallback(async () => {
    if (!hasWorkspaceAccess || missingBinding) {
      setLoading(false)
      return
    }

    setLoading(true)
    setErrorMessage('')
    setUnavailableMessage('')

    const [workersResponse, entriesResponse] = await Promise.all([
      supabase.from('workers').select('id, name, role').order('name'),
      supabase
        .from('employee_time_entries')
        .select('id, worker_id, job_id, entry_date, clocked_in_at, clocked_out_at, approval_status, manual_adjustment, adjustment_reason, note')
        .order('clocked_in_at', { ascending: false })
        .limit(400)
    ])

    if (workersResponse.error || entriesResponse.error) {
      if (isSchemaMismatchError(workersResponse.error) || isSchemaMismatchError(entriesResponse.error)) {
        setWorkersById(new Map())
        setJobsById(new Map())
        setEntries([])
        setUnavailableMessage(
          `${buildSchemaUnavailableMessage('Manager time administration', 'Workers and Calendar workspaces')} Required objects: workers table and employee_time_entries columns (id, worker_id, job_id, entry_date, clocked_in_at, clocked_out_at, approval_status, manual_adjustment, adjustment_reason, note).`
        )
        setLoading(false)
        return
      }

      setErrorMessage(workersResponse.error?.message || entriesResponse.error?.message || 'Unable to load time entries.')
      setLoading(false)
      return
    }

    const jobIds = [...new Set((entriesResponse.data || []).map((entry) => entry.job_id).filter(Boolean))]
    const jobsMap = new Map()
    if (jobIds.length > 0) {
      const jobsResponse = await supabase.from('jobs').select('id, job_type').in('id', jobIds)
      if (!jobsResponse.error) {
        ;(jobsResponse.data || []).forEach((job) => {
          jobsMap.set(String(job.id), job)
        })
      }
    }

    const workerMap = new Map()
    ;(workersResponse.data || []).forEach((worker) => workerMap.set(String(worker.id), worker))

    setWorkersById(workerMap)
    setJobsById(jobsMap)
    setEntries(entriesResponse.data || [])
    setLoading(false)
  }, [hasWorkspaceAccess, missingBinding])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const reviewQueueEntries = useMemo(() => {
    return entries.filter((entry) => {
      const dateKey = String(entry.entry_date || '').slice(0, 10)
      if (filterDate && filterDate !== dateKey) return false
      const status = String(entry.approval_status || 'pending').toLowerCase()
      return ['pending', 'needs_review', 'disputed'].includes(status) || entry.manual_adjustment === true
    })
  }, [entries, filterDate])

  const summary = useMemo(() => {
    const openPunches = reviewQueueEntries.filter((entry) => !entry.clocked_out_at).length
    const manualAdjustments = reviewQueueEntries.filter((entry) => entry.manual_adjustment === true).length
    return {
      reviewQueue: reviewQueueEntries.length,
      openPunches,
      manualAdjustments
    }
  }, [reviewQueueEntries])

  const handleOpenEditEntry = (entry) => {
    setEditingEntryId(String(entry.id))
    setEditClockedInAt(toDateTimeLocalValue(entry.clocked_in_at))
    setEditClockedOutAt(toDateTimeLocalValue(entry.clocked_out_at))
    setEditReason('')
    setEditNote(String(entry.note || ''))
    setEditError('')
  }

  const handleApplyEntryCorrection = async (entry) => {
    if (!editReason.trim()) {
      setEditError('Adjustment reason is required for manual time edits.')
      return
    }

    const correctedClockedInAt = toIsoOrNull(editClockedInAt) || entry.clocked_in_at
    const correctedClockedOutAt = toIsoOrNull(editClockedOutAt)

    if (correctedClockedOutAt && new Date(correctedClockedOutAt).getTime() < new Date(correctedClockedInAt).getTime()) {
      setEditError('Punch-out must be after punch-in.')
      return
    }

    setEditSaving(true)
    setEditError('')

    const response = await supabase.rpc('rpc_time_entry_apply_correction', {
      p_entry_id: entry.id,
      p_worker_id: entry.worker_id,
      p_clocked_in_at: correctedClockedInAt,
      p_clocked_out_at: correctedClockedOutAt,
      p_note: editNote || null,
      p_reason_code: `manager_manual_adjustment:${editReason.trim()}`
    })

    const payload = response.data || {}
    if (response.error || !payload?.success) {
      setEditSaving(false)
      setEditError(payload?.message || response.error?.message || 'Unable to apply manual correction.')
      return
    }

    setEditSaving(false)
    setEditingEntryId('')
    setEditClockedInAt('')
    setEditClockedOutAt('')
    setEditReason('')
    setEditNote('')
    await fetchData()
  }

  if (!hasWorkspaceAccess) {
    return <EmptyState title="Manager time administration unavailable" message="Role does not include manager time access." />
  }

  if (missingBinding) {
    return <ErrorState title="Manager binding required" message="Your account is missing a valid manager/shift-lead worker binding." />
  }

  if (loading) {
    return <LoadingState title="Loading manager time administration" message="Preparing punch records and review queue." />
  }

  if (unavailableMessage) {
    return (
      <TemplatePage className="gg-time-admin-canonical" data-testid="manager-time-admin">
        <TemplateHeaderBlock
          eyebrow="Manager"
          title="Time Administration"
          description="Review hours and apply manual corrections through auditable workflows."
          actions={(
            <button type="button" className="gg-button gg-button--secondary" onClick={() => fetchData()}>
              Retry
            </button>
          )}
        />

        <TemplateSectionCard
          label="Unavailable"
          title="Time admin data source unavailable"
          subtitle="Required data objects are missing in this environment."
        >
          <p className="gg-inline-alert gg-inline-alert--warning">{unavailableMessage}</p>
          <EmptyState
            title="Time administration unavailable"
            message="Manager time review cannot load in this environment right now."
          />
        </TemplateSectionCard>
      </TemplatePage>
    )
  }

  if (errorMessage) {
    return (
      <ErrorState
        title="Manager time administration unavailable"
        message={errorMessage}
        action={(
          <button type="button" className="gg-button gg-button--secondary" onClick={() => fetchData()}>
            Retry
          </button>
        )}
      />
    )
  }

  const editErrorTone = editError.toLowerCase().includes('unable') || editError.toLowerCase().includes('must be')
    ? 'gg-inline-alert--error'
    : 'gg-inline-alert--warning'

  return (
    <TemplatePage className="gg-time-admin-canonical" data-testid="manager-time-admin">
      <TemplateHeaderBlock
        eyebrow="Manager"
        title="Time Administration"
        description="Review hours, resolve punch discrepancies, and use manager/admin manual entry for audited corrections."
        actions={(
          <ActionButtonRow>
            <button type="button" className="gg-button gg-button--secondary" onClick={() => fetchData()}>
              Refresh
            </button>
          </ActionButtonRow>
        )}
      >
        <TemplateSummaryRow>
          <article className="gg-card">
            <p className="gg-card__title">Review Queue</p>
            <p className="gg-card__meta">{summary.reviewQueue}</p>
            <StatPill label="Needs review" value={summary.reviewQueue} tone={summary.reviewQueue ? 'warning' : 'info'} />
          </article>
          <article className="gg-card">
            <p className="gg-card__title">Open Punches</p>
            <p className="gg-card__meta">{summary.openPunches}</p>
            <StatPill label="Open" value={summary.openPunches} tone={summary.openPunches ? 'warning' : 'success'} />
          </article>
          <article className="gg-card">
            <p className="gg-card__title">Manual Adjustments</p>
            <p className="gg-card__meta">{summary.manualAdjustments}</p>
            <StatPill label="Adjusted" value={summary.manualAdjustments} tone={summary.manualAdjustments ? 'info' : 'success'} />
          </article>
        </TemplateSummaryRow>
      </TemplateHeaderBlock>

      <TemplateSectionCard
        label="Manual Entry"
        title="Manual Punch Entry"
        subtitle="Manager/admin manual punch entry only. This is not an employee self-serve clock screen."
      >
        <EmployeePunchIn allowWorkerSelection />
      </TemplateSectionCard>

      <TemplateSectionCard
        label="Review Queue"
        title="Time Review Queue"
        subtitle="Filter by operational date and apply correction actions as needed."
      >
        <FilterBar
          left={(
            <label className="gg-form-field">
              <span>Date</span>
              <input className="gg-input" type="date" value={filterDate} onChange={(event) => setFilterDate(event.target.value)} />
            </label>
          )}
          right={<span className="gg-inline-chip">Queue {reviewQueueEntries.length}</span>}
          summary={`${reviewQueueEntries.length} entries in review queue`}
        />

        {reviewQueueEntries.length === 0 ? (
          <EmptyState title="No review entries" message="No entries currently need review for this date." />
        ) : (
          <TemplateTableShell>
            <div className="gg-record-list">
              {reviewQueueEntries.map((entry) => {
                const worker = workersById.get(String(entry.worker_id))
                const job = jobsById.get(String(entry.job_id))
                const status = String(entry.approval_status || 'pending').toLowerCase()

                return (
                  <article key={entry.id} className="gg-record-item">
                    <div className="gg-record-item__head">
                      <div>
                        <p className="gg-record-item__title">{formatTitleText(worker?.name, 'Unknown Worker')}</p>
                        <p className="gg-record-item__meta">{formatTitleText(job?.job_type, 'Assigned Job')}</p>
                        <p className="gg-record-item__meta">
                          In {formatDateTime(entry.clocked_in_at)} • Out {entry.clocked_out_at ? formatDateTime(entry.clocked_out_at) : 'Open'}
                        </p>
                        {entry.manual_adjustment ? <p className="gg-record-item__meta">Manual adjustment: {entry.adjustment_reason || 'Reason not provided'}</p> : null}
                      </div>
                      <StatusBadge status={status} label={formatTitleText(status, 'Pending')} />
                    </div>

                    <ActionButtonRow>
                      <button type="button" className="gg-button gg-button--secondary" onClick={() => handleOpenEditEntry(entry)}>
                        Edit Punch
                      </button>
                    </ActionButtonRow>

                    {editingEntryId === String(entry.id) ? (
                      <TemplateFormShell>
                        <form className="gg-form-grid gg-form-grid--split" onSubmit={(event) => event.preventDefault()}>
                          <label className="gg-form-field">
                            <span>Clock In</span>
                            <input className="gg-input" type="datetime-local" value={editClockedInAt} onChange={(event) => setEditClockedInAt(event.target.value)} />
                          </label>
                          <label className="gg-form-field">
                            <span>Clock Out</span>
                            <input className="gg-input" type="datetime-local" value={editClockedOutAt} onChange={(event) => setEditClockedOutAt(event.target.value)} />
                          </label>
                          <label className="gg-form-field gg-form-field--wide">
                            <span>Adjustment Reason *</span>
                            <input
                              className="gg-input"
                              value={editReason}
                              onChange={(event) => setEditReason(event.target.value)}
                              placeholder="Required reason for manual adjustment"
                            />
                          </label>
                          <label className="gg-form-field gg-form-field--wide">
                            <span>Notes</span>
                            <textarea className="gg-textarea" value={editNote} onChange={(event) => setEditNote(event.target.value)} />
                          </label>
                          <div className="gg-form-actions gg-form-field--wide">
                            <button type="button" className="gg-button gg-button--primary" onClick={() => handleApplyEntryCorrection(entry)} disabled={editSaving}>
                              {editSaving ? 'Saving...' : 'Apply Manual Correction'}
                            </button>
                            <button type="button" className="gg-button gg-button--ghost" onClick={() => setEditingEntryId('')}>
                              Cancel
                            </button>
                          </div>
                        </form>
                        {editError ? <p className={`gg-inline-alert ${editErrorTone}`}>{editError}</p> : null}
                      </TemplateFormShell>
                    ) : null}
                  </article>
                )
              })}
            </div>
          </TemplateTableShell>
        )}
      </TemplateSectionCard>
    </TemplatePage>
  )
}
